import './ProfileCreation.scss';

import * as Yup from 'yup';

import { Button, Form, Modal, ModalBody } from 'react-bootstrap';
import { checkUser, createCandidate } from '../../ducks/entities';
import { useBeforeUnload, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Candidate } from '../../models/candidate';
import ControllerExperience from '../../components/ControllerExperience/ControllerExperience';
import { Formik } from 'formik';
import GamingGenres from '../../components/GamingGenres/GamingGenres';
import Language from '../../components/Language/Language';
import LoaderComponent from '../../components/Loader/LoaderComponents';
import Platforms from '../../components/Platforms/Platforms';
import ScrollToFieldError from '../../components/ScrollToFieldError/ScrollToFieldError';
import SelectQuestion from '../../components/SelectQuestion/SelectQuestion';
import WeeklyPlaytime from '../../components/WeeklyPlaytime/WeeklyPlaytime';
import { createLocalizedOptions } from '../../pages/root/App';
import messages from './messagesCreation';
import { playtestTypes } from '../../utils/constants';
import { selectLocalSettings } from '../../ducks/settings';
import siteMessages from '../root/siteMessages';
import { useIntl } from 'react-intl';
import { useToggle } from 'react-use';

interface OwnProps {
  setFormModified(value: boolean): void;
}

const ProfileCreation = (props: OwnProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showModal, toggleModal] = useToggle(false);
  const [schema, setSchema] = useState<any>();
  const [languageSchema, setLanguageSchema] = useState<any>();
  const [platformsSchema, setPlatformsSchema] = useState<any>();
  const [controllerExperienceSchema, setControllerExperienceSchema] =
    useState<any>();
  const [weeklyPlaytimeSchema, setWeeklyPlaytimeSchema] = useState<any>();
  const [gamingGenresSchema, setGamingGenresSchema] = useState<any>();
  const lang = useSelector(selectLocalSettings);
  const [isFormModified, setFormModified] = useState(false);
  const [validateOnChange, setValidateOnChange] = useState(false);

  const playtestTypesOptions = createLocalizedOptions(playtestTypes, messages);

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!isFormModified) return;
        event.preventDefault();
        event.returnValue = true;
      },
      [isFormModified]
    )
  );

  useEffect(() => {
    props.setFormModified(isFormModified);
  }, [isFormModified]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setSchema({
      ...languageSchema,
      ...platformsSchema,
      ...controllerExperienceSchema,
      ...weeklyPlaytimeSchema,
      ...gamingGenresSchema,
      playtestTypeIds: Yup.array(Yup.number()).min(
        1,
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      )
    });
  }, [languageSchema]);

  const onSubmit = async (values: Candidate, { setSubmitting }: any) => {
    setSubmitting(true);
    await (dispatch(createCandidate(values)) as any).then(() => {
      setSubmitting(false);
      setFormModified(false);
      toggleModal(true);
    });
  };

  const onSetField = (
    field: string,
    value: any,
    setFieldValue: any,
    shouldValidate?: boolean | undefined
  ) => {
    setFormModified(true);
    setFieldValue(field, value, shouldValidate);
  };

  return (
    <div className="creation">
      <Modal className="modal-creation" show={showModal} size="lg">
        <ModalBody>
          <h2 className="mx-4">
            {intl.formatMessage({ ...messages.confirmationTitle })}
          </h2>
          <div className="wrapper-bg">
            <span
              dangerouslySetInnerHTML={{
                __html: intl.formatMessage({ ...messages.confirmationSubTitle })
              }}
            />
          </div>
          <div className="wrapper-btn">
            <Button
              className="mb-2"
              variant="primary"
              onClick={() => {
                toggleModal(false);
                dispatch(checkUser());
              }}
            >
              {intl.formatMessage({ ...siteMessages.completeProfile })}
            </Button>
            <Button
              className="mb-3"
              variant="link"
              onClick={() => navigate(`/${lang}/`)}
            >
              {intl.formatMessage({ ...messages.finishLater })}
            </Button>
          </div>
          <p>{intl.formatMessage({ ...messages.confirmationFooter })}</p>
        </ModalBody>
      </Modal>
      <div className="form-header">
        <h2>{intl.formatMessage({ ...messages.title })}</h2>
        <p>{intl.formatMessage({ ...siteMessages.required })}</p>
      </div>
      <Formik
        initialValues={{
          playtestTypeIds: [],
          accessibilityIds: [],
          platformIds: [],
          controllerIds: [],
          weeklyPlaytimeId: -1,
          gamingGenreIds: [],
          languageId: -1,
          accessibilitiesOtherDetail: ''
        }}
        validationSchema={Yup.object().shape(schema)}
        onSubmit={onSubmit}
        validateOnChange={validateOnChange}
      >
        {({ values, errors, handleSubmit, isSubmitting, setFieldValue }) => (
          <Form
            onSubmit={(event) => {
              setValidateOnChange(true);
              event.preventDefault();
              handleSubmit();
            }}
            className="creation-form-content"
          >
            <ScrollToFieldError />
            <h4 className="question-title">
              1. {intl.formatMessage({ ...messages.languages })}
            </h4>
            <Language
              values={values}
              errors={errors}
              setFieldValue={(
                field: string,
                value: any,
                shouldValidate?: boolean | undefined
              ) => onSetField(field, value, setFieldValue, shouldValidate)}
              isEnglishAbility={false}
              hasLabel={false}
              updateSchema={(schema) => setLanguageSchema(schema)}
            />
            <h4 className="question-title">
              2. {intl.formatMessage({ ...messages.playtestTypes })}
            </h4>
            <SelectQuestion
              controlId="playtestTypeIds"
              isMulti={true}
              options={playtestTypesOptions}
              value={values.playtestTypeIds}
              error={errors.playtestTypeIds}
              setFieldValue={(
                field: string,
                value: any,
                shouldValidate?: boolean | undefined
              ) => onSetField(field, value, setFieldValue, shouldValidate)}
            />
            <h4 className="question-title">
              3. {intl.formatMessage({ ...messages.gaminghabits })}
            </h4>
            <Platforms
              values={values}
              errors={errors}
              setFieldValue={(
                field: string,
                value: any,
                shouldValidate?: boolean | undefined
              ) => onSetField(field, value, setFieldValue, shouldValidate)}
              updateSchema={(schema) => setPlatformsSchema(schema)}
            />
            <ControllerExperience
              values={values}
              errors={errors}
              setFieldValue={(
                field: string,
                value: any,
                shouldValidate?: boolean | undefined
              ) => onSetField(field, value, setFieldValue, shouldValidate)}
              updateSchema={(schema) => setControllerExperienceSchema(schema)}
            />
            <WeeklyPlaytime
              values={values}
              errors={errors}
              setFieldValue={(
                field: string,
                value: any,
                shouldValidate?: boolean | undefined
              ) => onSetField(field, value, setFieldValue, shouldValidate)}
              updateSchema={(schema) => setWeeklyPlaytimeSchema(schema)}
            />
            <GamingGenres
              values={values}
              errors={errors}
              setFieldValue={(
                field: string,
                value: any,
                shouldValidate?: boolean | undefined
              ) => onSetField(field, value, setFieldValue, shouldValidate)}
              updateSchema={(schema) => setGamingGenresSchema(schema)}
            />
            <div className="wrapper-btn mt-5">
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                <LoaderComponent isLoading={isSubmitting} className="lds-small">
                  {intl.formatMessage({ ...siteMessages.save })}
                </LoaderComponent>
              </Button>
            </div>
          </Form>
        )}
      </Formik>
      <div className="creation-form-footer">
        <p
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ ...messages.footer })
          }}
        />
      </div>
    </div>
  );
};

export default ProfileCreation;
